import { getTicketStatusColor, InsuranceStatus, Ticket } from '@/models/ticket';
import { StajackTableColumn } from '@/components/Table/v2/Table';
import { LiftOperator } from '@/models/liftoperator';
import { Chip } from '@mui/material';
import { formatDateToString } from '@/components/TicketsOverviewUI/ticketUiUtils';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTicketLabelTranslations } from '@/components/TicketsOverviewUI/hooks/useTicketLabelTranslations';

export const ticketTableColumnIds = [
    'orderReference',
    'email',
    'ticketOwnerData',
    'name',
    'orderDisplayId',
    'orderDate',
    'liftOperator',
    'ticketName',
    'status',
    'insurance',
] as const;

export type TicketTableColumnId = (typeof ticketTableColumnIds)[number];

type TicketTableColumn = {
    columnId: TicketTableColumnId;
} & StajackTableColumn<Ticket>;

export const useTicketColumns = ({
    liftOperators,
}: {
    liftOperators: LiftOperator[];
}): TicketTableColumn[] => {
    const { t, i18n } = useTranslation();
    const { getOrderStatusAsString } = useTicketLabelTranslations();

    const getInsuranceLabel = useCallback(
        (ticket: Ticket) => {
            if (!ticket.insurance) return t('common.no');

            if (ticket.insurance.canceledAt) return t('insurance.canceled');

            if (ticket.insurance.revokedAt) return t('insurance.revoked');

            if (ticket.insurance.status === InsuranceStatus.BOOKED)
                return t('insurance.bookedWithTariff', {
                    tariffName: ticket.insurance.tariffName[i18n.language],
                });
        },
        [t]
    );

    return useMemo(
        () => [
            {
                label: t('ticketColumns.referenceNumber'),
                itemKey: 'orderReference',
                columnId: 'orderReference',
            },

            {
                label: t('ticketColumns.emailBooker'),
                itemKey: 'email',
                columnId: 'email',
            },

            {
                label: t('ticketColumns.ticketBuyer'),
                itemTransform: (ticket) =>
                    `${ticket.lastname} ${ticket.firstname}
                ${
                    ticket.confirmationNumber
                        ? ` (${ticket.confirmationNumber})`
                        : ''
                }
             `,
                columnId: 'ticketOwnerData',
            },

            {
                label: t('ticketColumns.starjackOrderNumber'),
                itemKey: 'orderDisplayId',
                columnId: 'orderDisplayId',
            },

            {
                label: t('ticketColumns.orderDate'),
                itemTransform: (ticket) =>
                    formatDateToString(ticket.orderDate, { detailed: true }) ||
                    '-',
                columnId: 'orderDate',
            },

            {
                label: t('ticketColumns.liftOperator'),
                itemTransform: (ticket) =>
                    liftOperators.find((l) => l.id === ticket.liftOperatorId)
                        ?.name[i18n.language] || '-',
                columnId: 'liftOperator',
            },

            {
                label: t('ticketColumns.ticketName'),
                itemKey: 'name',
                columnId: 'ticketName',
            },

            {
                label: t('ticketColumns.insuranceBooked'),
                columnId: 'insurance',
                itemTransform: (ticket) => (
                    <Chip
                        color={
                            ticket.insurance &&
                            ticket.insurance.status === InsuranceStatus.BOOKED
                                ? 'success'
                                : 'error'
                        }
                        label={getInsuranceLabel(ticket)}
                    />
                ),
            },

            {
                label: t('ticketColumns.status'),
                itemTransform: (ticket) => (
                    <Chip
                        color={getTicketStatusColor(ticket.status)}
                        label={getOrderStatusAsString(ticket.status)}
                    />
                ),
                columnId: 'status',
            },
        ],
        [t, liftOperators, getInsuranceLabel]
    );
};
